import { apiErrorMessage } from '@agos/api-client'
import {
  getGetNotificationsQueryKey,
  getGetNotificationsUnreadCountQueryKey,
  useGetNotifications,
  useGetNotificationsUnreadCount,
  usePostNotificationsDailyCheck,
  usePostNotificationsIdRead,
  usePostNotificationsReadAll,
} from '@agos/api-client/notifications'
import { T, formatCount, formatDateTime, tt, useAuth } from '@agos/shared'
import { ConfirmDialog, EmptyState, PageHeader, Spinner, cn, useToast, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  AlertTriangle,
  Bell,
  CheckCircle2,
  Eye,
  Info,
  RefreshCw,
} from 'lucide-react'
import { useState } from 'react'
import { Link } from 'wouter'

import { mayWrite } from '../write-access'
import {
  PAGE_SIZE,
  agoLabel,
  countsDescription,
  dailyCheckWarning,
  inboxGroups,
  inboxSummary,
  hasMore,
  isLabelledKind,
  linkFor,
  nextPageSize,
  pageOf,
  severityLabel,
  severityFilterIsUseful,
  severityTallies,
  notificationSubject,
  type Notification,
  type Severity,
} from './notification-inbox'

/**
 * `/notifications` — the staff inbox.
 *
 * Not `/settings-notifications`, which decides how *customers* are told things. This one is what the system
 * noticed: low stock, an invoice past due, a purchase order it drafted. Same controller, different job, so
 * two pages.
 *
 * **FB-146.** The unread count was computed from the loaded page and printed beside the server's total, so
 * one sentence carried two different populations. `GET /notifications/unread-count` answers the real figure
 * and is used.
 *
 * **FB-147.** `<T>{n.title}</T>` put generated data through the translation memory — every notification a
 * dictionary key, and a call to the missing-translation endpoint for each. The titles are written in Slovak
 * by the backend; they are rendered as text.
 *
 * **And the daily check says what it does first.** `DailyCheck.run()` drafts purchase orders and e-mails
 * every administrator; legacy's button fires on the first click.
 *
 * Ported from legacy `pages/notifications.tsx` (185 lines).
 */

export function NotificationsPage() {
  const [offset, setOffset] = useState(0)
  const [unreadOnly, setUnreadOnly] = useState(false)
  // §9.423 - which severity is in force. Client-side over the loaded rows, because the list endpoint
  // takes `unread`, `limit` and `offset` and no severity; `countsDescription` says how many of the
  // total are shown, so the two figures read together honestly.
  const [severity, setSeverity] = useState<Severity | 'all'>('all')
  const [loaded, setLoaded] = useState<Notification[]>([])
  const [confirmCheck, setConfirmCheck] = useState(false)

  const { toast } = useToast()
  const { user, hasPermission } = useAuth()
  const queryClient = useQueryClient()

  const list = useGetNotifications({
    limit: PAGE_SIZE,
    offset,
    ...(unreadOnly ? { unread: 'true' } : {}),
  })
  const unread = useGetNotificationsUnreadCount()

  const page = pageOf(list.data)
  // The first page is the query's own; later pages accumulate. Kept separate so a refetch of page one
  // cannot silently truncate what is already on screen.
  const rows = offset === 0 ? page.notifications : [...loaded, ...page.notifications]

  const mayMark = mayWrite('notifications-inbox', user?.systemRole, hasPermission)
  const mayRunCheck = user?.systemRole === 'main_admin'

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetNotificationsQueryKey() })
    void queryClient.invalidateQueries({ queryKey: getGetNotificationsUnreadCountQueryKey() })
  }

  // §9.395 - *Skúste to znova* is advice, not a reason. The server's sentence where there is one.
  const failed = (caught: unknown) =>
    toast({
      variant: 'destructive',
      title: tt('Nepodarilo sa to'),
      description: apiErrorMessage(caught, tt('Skúste to znova.')),
    })

  const markOne = usePostNotificationsIdRead({ mutation: { onSuccess: refresh, onError: failed } })
  const markAll = usePostNotificationsReadAll({
    mutation: {
      onSuccess: () => {
        setOffset(0)
        setLoaded([])
        refresh()
      },
      onError: failed,
    },
  })
  const dailyCheck = usePostNotificationsDailyCheck({
    mutation: {
      onSuccess: (result) => {
        const created = (result as { notificationsCreated?: unknown } | undefined)?.notificationsCreated
        setOffset(0)
        setLoaded([])
        refresh()
        toast({
          title: tt('Kontrola dokončená'),
          description:
            typeof created === 'number'
              ? `${tt('Nových oznámení')}: ${formatCount(created)}`
              : tt('Server nepovedal, koľko oznámení vzniklo.'),
        })
      },
      onError: failed,
    },
  })

  const counts = {
    shown: rows.length,
    total: page.total,
    unread:
      typeof (unread.data as { count?: unknown } | undefined)?.count === 'number'
        ? ((unread.data as { count: number }).count)
        : null,
  }

  // §9.348, the owner's C6. What needs doing, above the list rather than inside it: a flat list newest
  // first is right for a few notices and useless for 151, which is what this inbox has held.
  const summary = inboxSummary(loaded)

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Oznámenia')}
        icon={<Bell className="w-5 h-5" />}
        description={countsDescription(counts)}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {mayRunCheck && (
              <button
                type="button"
                data-run-check
                disabled={dailyCheck.isPending}
                onClick={() => setConfirmCheck(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-card border border-border text-sm font-medium text-muted-foreground disabled:opacity-50"
              >
                {dailyCheck.isPending ? <Spinner /> : <RefreshCw className="w-4 h-4" />}
                <T>Spustiť dennú kontrolu</T>
              </button>
            )}
            {counts.unread !== null && counts.unread > 0 && mayMark && (
              <button
                type="button"
                data-mark-all
                disabled={markAll.isPending}
                onClick={() => markAll.mutate()}
                className={cn('flex items-center gap-1.5 px-3 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
              >
                <CheckCircle2 className="w-4 h-4" /> <T>Označiť všetky</T>
              </button>
            )}
          </div>
        }
      />

      {summary !== '' && (
        <div
          className="rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3"
          data-inbox-summary
        >
          <p className="text-sm font-semibold">{summary}</p>
        </div>
      )}

      {/* §9.424 - **one row of controls, and the severity chips only when they distinguish
        * anything.** §9.423 drew two rows of chips and, on the owner's data, two of them read the
        * same number: *Všetky 151* beside *Upozornenia 151*, because every notice was a warning.
        * That is how a filter row becomes clutter that looks like a fault. */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          { id: 'all', label: tt('Všetky'), only: false },
          { id: 'unread', label: tt('Iba neprečítané'), only: true },
        ].map((filter) => (
          <button
            key={filter.id}
            type="button"
            data-filter={filter.id}
            data-filter-active={unreadOnly === filter.only ? 'yes' : 'no'}
            onClick={() => {
              setUnreadOnly(filter.only)
              setOffset(0)
              setLoaded([])
            }}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors',
              unreadOnly === filter.only
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card border-border text-muted-foreground hover:text-foreground',
            )}
          >
            {filter.label}
          </button>
        ))}

        {severityFilterIsUseful(rows) && (
          <>
            <span className="w-px h-5 bg-border mx-1" />
            <div className="flex flex-wrap gap-2" data-severity-filter>
              {severityTallies(rows).map((tally) => (
                <button
                  key={tally.severity}
                  type="button"
                  data-severity-chip={tally.severity}
                  data-severity-active={severity === tally.severity ? 'yes' : 'no'}
                  onClick={() => setSeverity(tally.severity)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors',
                    severity === tally.severity
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {severityLabel(tally.severity)}
                  <span
                    className={cn(
                      'px-1.5 rounded-full text-[10px] tabular-nums',
                      severity === tally.severity ? 'bg-black/15' : 'bg-muted',
                    )}
                    data-severity-count={tally.severity}
                  >
                    {formatCount(tally.count)}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {list.isLoading && rows.length === 0 ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Bell className="w-12 h-12" />}
          title={tt('Žiadne oznámenia')}
          description={
            unreadOnly ? tt('Všetko je prečítané.') : tt('Systém si zatiaľ nič nevšimol.')
          }
        />
      ) : (
        <div className="space-y-4">
          {/* §9.424 - **grouped by what they are about, which is where the repetition was.** All 151
            * of the owner's notices share one title prefix - *Sklad pod minimum* - so the prefix
            * belongs on the heading once and the item belongs in the row. §9.423 grouped by day
            * instead: twelve headings over twelve near-identical rows each, every one still
            * repeating the prefix and a kind chip reading *Sklad*.
            *
            * Worst severity first, then largest: two expired vehicle documents belong above 151
            * low-stock warnings however many of the latter there are. */}
          {inboxGroups(
            rows.filter((one) => severity === 'all' || one.severity === severity),
          ).map((group) => (
            <div key={group.key} data-inbox-group={group.key || 'other'} className="space-y-1">
              <div className="flex items-center gap-2">
                <span
                  className={cn('w-1.5 h-1.5 rounded-full shrink-0', GROUP_DOT[group.severity])}
                  aria-hidden="true"
                />
                {/* §9.424 - `data-kind` was on every row and is a group-level fact now: the kind
                  * is what the group *is*. `settings-notifications.spec.ts` follows it here. */}
                <h2
                  className="text-xs font-bold text-foreground"
                  data-kind={isLabelledKind(group.kind) ? 'labelled' : 'raw'}
                >
                  {group.label}
                </h2>
                <span className="text-[11px] text-muted-foreground tabular-nums" data-group-count>
                  {formatCount(group.notifications.length)}
                </span>
                <span className="flex-1 h-px bg-border" />
              </div>
              <div className="rounded-xl border border-border bg-card overflow-hidden divide-y divide-border">
                {group.notifications.map((notification) => (
                  <Row
                    key={notification.id}
                    notification={notification}
                    mayMark={mayMark}
                    onMark={() => markOne.mutate({ id: notification.id })}
                  />
                ))}
              </div>
            </div>
          ))}

          {hasMore(counts) && (
            <button
              type="button"
              data-load-more
              disabled={list.isFetching}
              onClick={() => {
                setLoaded(rows)
                setOffset(rows.length)
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-dashed border-border text-sm font-bold text-muted-foreground disabled:opacity-50 mt-3"
            >
              {list.isFetching ? <Spinner /> : <RefreshCw className="w-4 h-4" />}{' '}
              <T>Načítať ďalších</T> {formatCount(nextPageSize(counts))}
            </button>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmCheck}
        onClose={() => setConfirmCheck(false)}
        title={tt('Spustiť dennú kontrolu teraz?')}
        description={dailyCheckWarning()}
        confirmLabel={tt('Spustiť')}
        icon={<RefreshCw className="w-5 h-5" />}
        loading={dailyCheck.isPending}
        onConfirm={() => {
          setConfirmCheck(false)
          dailyCheck.mutate()
        }}
      />
    </div>
  )
}

/**
 * Severity as an accent, not as a wash - §9.423.
 *
 * **The owner:** *"can you make sure notifications will be better? This looks like an notification
 * panel from 2002... in maaanger website. I hate it, sooo much."*
 *
 * It did, and this table was why. Each entry used to be
 * `bg-blue-500/10 border-blue-500/30 **text-blue-500**` applied to the row's own container, so the
 * colour was not an accent - it was inherited by the title, the body, the kind and the timestamp.
 * Twenty-five of those stacked is a column of solid blue, amber and red blocks with coloured body
 * text in them, which is exactly the alert-box idiom the owner is describing.
 *
 * Now the row is an ordinary card and the severity appears twice, both times small: a four-pixel
 * left accent and the icon's own chip. The words are read in the foreground colour like every other
 * list in the product.
 */
/** The group heading's dot - §9.424. The same three colours, at the size a heading wants. */
const GROUP_DOT: Record<Severity, string> = {
  info: 'bg-blue-500',
  warning: 'bg-amber-500',
  error: 'bg-red-500',
}

const SEVERITY: Record<Severity, { text: string; icon: typeof Info }> = {
  info: { text: 'text-blue-500', icon: Info },
  warning: { text: 'text-amber-500', icon: AlertTriangle },
  error: { text: 'text-red-500', icon: AlertCircle },
}

function Row({
  notification,
  mayMark,
  onMark,
}: {
  notification: Notification
  mayMark: boolean
  onMark: () => void
}) {
  const style = SEVERITY[notification.severity]
  const Icon = style.icon
  const link = linkFor(notification)
  const isRead = notification.readAt !== null

  return (
    <div
      data-notification={notification.id}
      data-severity={notification.severity}
      data-read={isRead ? 'yes' : 'no'}
      /* §9.424 - **one line per notice, inside the group's card.** §9.423 made every notice its own
       * bordered card with an icon tile, a meta line, a title and a body: four lines of chrome, times
       * 151. The category is on the group heading now, so the row carries what actually differs -
       * the item, what is wrong with it, and when. */
      className={cn(
        'group flex items-center gap-2.5 px-3 py-2 transition-colors hover:bg-muted/40',
        isRead && 'bg-muted/20',
      )}
    >
      <Icon className={cn('w-3.5 h-3.5 shrink-0', isRead ? 'text-muted-foreground' : style.text)} />

      <span className="min-w-0 flex-1 flex flex-wrap items-baseline gap-x-2">
        {/* The subject, which is the only part that differs between 151 low-stock notices. */}
        <span
          className={cn(
            'text-sm truncate',
            isRead ? 'font-medium text-muted-foreground' : 'font-semibold text-foreground',
          )}
          data-subject
        >
          {notificationSubject(notification.title)}
        </span>
        {notification.body !== null && notification.body !== '' && (
          <span className="text-xs text-muted-foreground truncate">{notification.body}</span>
        )}
      </span>

      {/* §9.424 - the kind chip is gone. It read *Sklad* on all 151 rows, which is what the group
        * heading now says once. What is kept is the time, because that does differ. */}
      <span
        className="shrink-0 text-[11px] text-muted-foreground tabular-nums"
        title={formatDateTime(notification.createdAt)}
        data-ago
      >
        {agoLabel(notification.createdAt) || formatDateTime(notification.createdAt)}
      </span>

      {!isRead && (
        <span
          data-unread-dot
          aria-hidden="true"
          className="w-1.5 h-1.5 rounded-full bg-primary shrink-0"
        />
      )}

      {/* The actions stay reachable - a keyboard user tabs to them and they must not be hidden
        * behind a hover. Only their contrast lifts on hover. */}
      <span className="flex items-center gap-0.5 shrink-0">
        {link !== null && (
          <Link
            href={link}
            data-open
            aria-label={tt('Zobraziť')}
            onClick={() => {
              if (!isRead && mayMark) onMark()
            }}
            className="p-1 rounded-md text-muted-foreground/60 hover:text-foreground hover:bg-muted group-hover:text-muted-foreground transition-colors"
          >
            <Eye className="w-3.5 h-3.5" />
          </Link>
        )}
        {!isRead && mayMark && (
          <button
            type="button"
            data-mark-read
            aria-label={tt('Označiť ako prečítané')}
            onClick={onMark}
            className="p-1 rounded-md text-muted-foreground/60 hover:text-foreground hover:bg-muted group-hover:text-muted-foreground transition-colors"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
          </button>
        )}
      </span>
    </div>
  )
}
